import React, { useState, useRef, useCallback, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import type { ProgressUpdate } from "../services/claudeService";
import ProgressBar from "./ProgressBar";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export type EventInputMode = "text" | "file";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = "image/*,.pdf";
const SUPPORTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

interface EventInputProps {
  userInput: string;
  setUserInput: (value: string) => void;
  fileContent: string | null;
  setFileContent: (value: string | null) => void;
  fileMediaType: string | null;
  setFileMediaType: (value: string | null) => void;
  inputMode: EventInputMode;
  setInputMode: (mode: EventInputMode) => void;
  onParse: () => void;
  isLoading: boolean;
  progress: ProgressUpdate | null;
  displayProgress: number;
  error: string | null;
  successMessage: string | null;
}

const EventInput: React.FC<EventInputProps> = ({
  userInput,
  setUserInput,
  fileContent,
  setFileContent,
  fileMediaType,
  setFileMediaType,
  inputMode,
  setInputMode,
  onParse,
  isLoading,
  progress,
  displayProgress,
  error,
  successMessage,
}) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clean up object URLs and file input when fileContent is cleared externally (e.g. on cancel)
  // Display state (fileName, filePreviewUrl, fileError) doesn't need clearing here because
  // the render gates on fileContent — stale values aren't visible when fileContent is null,
  // and processFile overwrites them when a new file is selected.
  const prevFileContentRef = useRef(fileContent);
  useEffect(() => {
    if (prevFileContentRef.current && !fileContent) {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    prevFileContentRef.current = fileContent;
  }, [fileContent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render the first page of a PDF to a preview image URL
  const renderPdfPreview = useCallback(async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);

    // Render at a reasonable scale for preview
    const scale = 1.5;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not create canvas context");

    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL("image/png");
  }, []);

  // Convert an image blob to PNG via canvas (for unsupported formats like HEIC)
  const convertImageToPng = useCallback(
    (file: File): Promise<{ dataUrl: string; mediaType: string }> => {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Could not create canvas context"));
            return;
          }
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          URL.revokeObjectURL(objectUrl);
          resolve({ dataUrl, mediaType: "image/png" });
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Failed to load image for conversion"));
        };
        img.src = objectUrl;
      });
    },
    []
  );

  const resetFileState = useCallback(() => {
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    setFileContent(null);
    setFileMediaType(null);
    setFileName(null);
    setFilePreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [filePreviewUrl, setFileContent, setFileMediaType]);

  const processFile = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        setFileError(
          `File too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum is 10MB.`
        );
        return;
      }

      const isImage = file.type.startsWith("image/");
      const isPdf = file.type === "application/pdf";

      if (!isImage && !isPdf) {
        setFileError("Please select an image (PNG, JPG, etc.) or PDF file.");
        return;
      }

      setFileName(file.name);
      setFileError(null);

      if (isImage) {
        setFilePreviewUrl(URL.createObjectURL(file));

        if (SUPPORTED_IMAGE_TYPES.has(file.type)) {
          // Natively supported — read directly
          const reader = new FileReader();
          reader.onload = (event) => {
            setFileContent(event.target?.result as string);
            setFileMediaType(file.type);
          };
          reader.onerror = () => {
            resetFileState();
            setFileError("Failed to read the file. Please try again.");
          };
          reader.readAsDataURL(file);
        } else {
          // Unsupported format (HEIC, TIFF, BMP, etc.) — convert to PNG via canvas
          convertImageToPng(file)
            .then(({ dataUrl, mediaType }) => {
              setFileContent(dataUrl);
              setFileMediaType(mediaType);
            })
            .catch(() => {
              resetFileState();
              setFileError(
                "Could not process this image format. Please convert it to JPG or PNG and try again."
              );
            });
        }
      } else {
        // PDF — render first page preview, then read as data URL
        renderPdfPreview(file)
          .then((previewDataUrl) => {
            setFilePreviewUrl(previewDataUrl);
          })
          .catch(() => {
            // Preview failed but file can still be processed — show no preview
            setFilePreviewUrl(null);
          });

        const reader = new FileReader();
        reader.onload = (event) => {
          setFileContent(event.target?.result as string);
          setFileMediaType(file.type);
        };
        reader.onerror = () => {
          resetFileState();
          setFileError("Failed to read the file. Please try again.");
        };
        reader.readAsDataURL(file);
      }
    },
    [setFileContent, setFileMediaType, convertImageToPng, resetFileState, renderPdfPreview]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleClearFile = useCallback(() => {
    resetFileState();
    setFileError(null);
  }, [resetFileState]);

  const hasInput = inputMode === "text" ? userInput.trim() : fileContent;

  return (
    <div className="flex flex-col h-full">
      {/* Header row with label + mode selector */}
      <div className="flex items-center justify-between pb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">
            {inputMode === "text" ? "Describe your event(s)" : "Upload event details"}
          </h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {inputMode === "text"
              ? "Type or paste event details and AI will create calendar entries."
              : "Upload an invitation, flyer, or screenshot to extract event details."}
          </p>
        </div>
        <div className="inline-flex rounded bg-navy/80 border border-border-strong p-1 shrink-0 ml-4">
          <button
            onClick={() => setInputMode("text")}
            disabled={isLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
              inputMode === "text"
                ? "bg-rose-dim text-rose-accent"
                : "text-text-secondary hover:text-white"
            } disabled:opacity-50`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Text
          </button>
          <button
            onClick={() => setInputMode("file")}
            disabled={isLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
              inputMode === "file"
                ? "bg-rose-dim text-rose-accent"
                : "text-text-secondary hover:text-white"
            } disabled:opacity-50`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Upload File
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
        {/* Text Input */}
        {inputMode === "text" && (
          <textarea
            id="event-input"
            value={userInput}
            onChange={(e: { target: { value: string } }) => setUserInput(e.target.value)}
            placeholder={`Try something like:\n\n"Team meeting tomorrow at 10am to 11am to discuss Q3 results. Remind me 15 minutes before."\n\n"Lunch with Sarah at The Cafe on Friday at 1pm."`}
            className="flex-1 w-full bg-navy/50 border border-border-strong rounded p-4 text-text-primary text-sm leading-relaxed placeholder-text-muted focus:ring-2 focus:ring-rose-accent/30 focus:border-rose-accent/40 focus:outline-none transition-all resize-none"
            disabled={isLoading}
          />
        )}

        {/* File Upload */}
        {inputMode === "file" && (
          <div className="flex-1 flex flex-col gap-3">
            {/* Info notice */}
            <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-sm bg-amber-500/10 border border-amber-500/20">
              <svg
                className="w-4 h-4 text-amber-400 shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-amber-300/90">
                File uploads are for <span className="font-medium">adding new events</span> only
                (e.g. invitations, flyers, screenshots). To modify or cancel existing events, use the
                text input.
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileSelect}
              className="hidden"
              disabled={isLoading}
            />

            {!fileContent ? (
              /* Drop zone - shown when no file selected */
              <div
                onClick={() => !isLoading && fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`flex-1 flex flex-col items-center justify-center rounded border-2 border-dashed transition-all cursor-pointer ${
                  isDragOver
                    ? "border-rose-accent bg-rose-glow"
                    : "border-border-strong bg-navy/30 hover:border-border-strong hover:bg-navy/50"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex flex-col items-center gap-3 p-6">
                  <div
                    className={`w-12 h-12 rounded flex items-center justify-center transition-colors ${
                      isDragOver ? "bg-rose-dim" : "bg-navy-mid/50"
                    }`}
                  >
                    <svg
                      className={`w-6 h-6 transition-colors ${isDragOver ? "text-rose-accent" : "text-text-secondary"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-text-primary">
                      <span className="text-rose-accent font-medium">Click to upload</span> or drag
                      and drop
                    </p>
                    <p className="text-xs text-text-muted mt-1">
                      Images (PNG, JPG, HEIC) or PDF up to 10MB
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* File preview - shown when file is selected */
              <div className="flex-1 flex flex-col rounded border border-border-strong bg-navy/30 overflow-hidden">
                {/* File header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
                  <div className="flex items-center gap-3 min-w-0">
                    {fileMediaType === "application/pdf" ? (
                      <div className="w-8 h-8 rounded-sm bg-red-500/15 flex items-center justify-center shrink-0">
                        <svg
                          className="w-4 h-4 text-red-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-sm bg-rose-glow flex items-center justify-center shrink-0">
                        <svg
                          className="w-4 h-4 text-rose-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{fileName}</p>
                      <p className="text-xs text-text-muted">
                        {fileMediaType === "application/pdf" ? "PDF document" : "Image file"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClearFile}
                    disabled={isLoading}
                    className="p-1.5 rounded-sm text-text-secondary hover:text-white hover:bg-navy-mid transition-colors disabled:opacity-50"
                    title="Remove file"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Preview content */}
                {filePreviewUrl && (
                  <div className="flex items-center justify-center p-4">
                    <img
                      src={filePreviewUrl}
                      alt="Uploaded file preview"
                      className="max-w-sm max-h-72 rounded-sm object-contain"
                    />
                  </div>
                )}
                {!filePreviewUrl && (
                  <div className="flex-1 flex items-center justify-center p-6">
                    <div className="flex flex-col items-center gap-2 text-text-muted">
                      <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-xs">Ready for processing</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* File error */}
            {fileError && (
              <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-sm bg-red-500/10 border border-red-500/30">
                <svg
                  className="w-4 h-4 text-red-400 shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-sm text-red-300">{fileError}</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Error / Success / Progress / Button - always visible at bottom */}
      <div className="shrink-0 pt-4 space-y-3">
        {error && (
          <div className="p-3 bg-red-900/50 border border-red-700 text-red-300 rounded-sm text-center text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="p-3 bg-green-900/50 border border-green-700 text-green-300 rounded-sm text-center text-sm">
            {successMessage}
          </div>
        )}
        {isLoading && progress ? (
          <div className="p-4 bg-navy-darkest border border-border-strong rounded">
            <ProgressBar percent={displayProgress} message={progress.message} />
          </div>
        ) : (
          <button
            onClick={onParse}
            disabled={isLoading || !hasInput}
            className="w-full flex justify-center items-center gap-2 bg-navy-mid hover:bg-navy-light disabled:bg-navy-deep/50 disabled:cursor-not-allowed disabled:text-text-muted text-white font-bold py-3 px-4 rounded transition-colors text-lg border border-border-strong focus:outline-none focus:ring-2 focus:ring-rose-accent focus:ring-offset-2 focus:ring-offset-navy-darkest"
          >
            Parse Events
          </button>
        )}
      </div>
    </div>
  );
};

export default EventInput;
